import React, { forwardRef, useId } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '../theme';

// ============================================================================
// CHECKBOX
// ============================================================================

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  /**
   * Label for the checkbox.
   */
  label?: string;
  /**
   * Description text below the label.
   */
  description?: string;
  /**
   * Error message.
   */
  error?: string;
  /**
   * If true, shows indeterminate state.
   */
  indeterminate?: boolean;
}

/**
 * Checkbox component with label and description support.
 * 
 * @example
 * // Basic checkbox
 * <Checkbox label="Accept terms" />
 * 
 * // With description
 * <Checkbox 
 *   label="Marketing emails"
 *   description="Receive updates about new features and promotions"
 * />
 * 
 * // Controlled
 * <Checkbox 
 *   checked={isChecked}
 *   onCheckedChange={setIsChecked}
 *   label="Remember me"
 * />
 */
const Checkbox = forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ 
  className, 
  label, 
  description, 
  error,
  indeterminate = false,
  id: providedId,
  disabled,
  ...props 
}, ref) => {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const hasError = !!error;

  const checkboxElement = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={id}
      disabled={disabled}
      className={cn(
        'peer h-5 w-5 shrink-0 rounded border-2',
        'border-warm-gray/40 bg-white',
        'transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
        'hover:border-sage-light',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:border-sage data-[state=checked]:bg-sage',
        'data-[state=indeterminate]:border-sage data-[state=indeterminate]:bg-sage',
        hasError && 'border-red-400 focus-visible:ring-red-500',
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn('flex items-center justify-center text-white')}
      >
        {indeterminate ? (
          <Minus className="h-3.5 w-3.5" strokeWidth={3} />
        ) : (
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label && !description) {
    return checkboxElement;
  }

  return (
    <div className="flex items-start gap-3">
      {checkboxElement}
      <div className="grid gap-0.5 leading-none">
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'text-sm font-medium text-navy cursor-pointer',
              'peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
              disabled && 'opacity-70 cursor-not-allowed'
            )}
          >
            {label}
          </label>
        )}
        {description && (
          <p className="text-sm text-warm-gray">
            {description}
          </p>
        )}
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
      </div>
    </div>
  );
});

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

// ============================================================================
// EXPORTS
// ============================================================================

export { Checkbox };
